import React, { ForwardedRef, ThHTMLAttributes } from 'react';
import clsx from 'clsx';

import styles from '@danske/sapphire-css/components/table/table.module.css';
import { DOMAttributes, SortDirection } from '@react-types/shared';
import { Icon } from '../../Icon';
import { SortDescending, SortAscending } from '@danske/sapphire-icons/react';
import { ColStyleProps, ColumnAlignment } from '../index';
import { useFocusRing } from '@react-aria/focus';
import { useHover } from '@react-aria/interactions';
import { getAlignmentStyles } from './utils';
import { mergeProps, mergeRefs } from '@react-aria/utils';
import { TooltipIfNeeded } from './TooltipIfNeeded';
import { SapphireStyleProps, useSapphireStyleProps } from '../../utils';

export type TableHeaderCellProps = Omit<
  DOMAttributes<HTMLTableHeaderCellElement>,
  'className' | 'style' | 'dangerouslySetInnerHTML'
> & {
  children?: React.ReactNode;
  /**
   * Note: Currently, we don't officially support non-table elements.
   *
   * @default 'th'
   */
  elementType?: 'th';

  render?: (
    props: DOMAttributes<HTMLElement> & { ref: ForwardedRef<any> }
  ) => React.ReactElement;

  /** Whether the column allows sorting. */
  allowsSorting?: boolean;

  sortDirection?: SortDirection;

  // TODO(v6): remove
  /** If the sorting indicator should be displayed
   * @deprecated using the right combination of allowsSorting and sortDirection is enough
   **/
  isActiveSort?: boolean;

  /**
   * Tooltip text that's shown when overflow is set to "truncate" and the cell content doesn't fit.
   * Useful when the cell content is not plain text. If not provided, the text content of the cell
   * is used.
   */
  textValue?: React.ReactNode;

  // TODO(v6): remove null.
  width?: SapphireStyleProps['width'] | null;
  minWidth?: SapphireStyleProps['minWidth'] | null;
  maxWidth?: SapphireStyleProps['maxWidth'] | null;
} & ColStyleProps &
  Pick<
    ThHTMLAttributes<HTMLTableHeaderCellElement>,
    'scope' | 'headers' | 'abbr' | 'colSpan' | 'rowSpan'
  > &
  SapphireStyleProps;

/**
 * Corresponding to `th` element in native html. Should be rendered inside
 * {@Link Table.HeaderRow}
 */

export const TableHeaderCell = React.forwardRef(function TableHeaderCell(
  {
    render: renderProp,
    children,
    elementType,
    align,
    sortDirection,
    allowsSorting,
    isActiveSort,
    overflow = 'truncate',
    textValue,
    colSpan,
    rowSpan,
    ...props
  }: TableHeaderCellProps,
  forwardedRef: ForwardedRef<HTMLTableCellElement>
) {
  const { focusProps, isFocusVisible } = useFocusRing();
  const { hoverProps, isHovered } = useHover({});
  const { styleProps, filteredProps } = useSapphireStyleProps(props);

  const ElementType = elementType || 'th';
  const role = props.role || ElementType === 'th' ? undefined : 'columnheader';
  const ariaSort =
    props['aria-sort'] ||
    (allowsSorting && isActiveSort ? sortDirection : undefined);

  return (
    <ElementType
      ref={forwardedRef}
      {...mergeProps(focusProps, hoverProps, filteredProps, {
        role,
        'aria-sort': ariaSort,
        colSpan,
        rowSpan,
      })}
      className={clsx(
        headerCellClasses({
          align: align,
          allowsSorting: allowsSorting,
          sortDirection: isActiveSort ? sortDirection : undefined,
          isFocusVisible: isFocusVisible,
          isHovered: isHovered,
        }),
        styleProps.className
      )}
      style={styleProps.style}
    >
      <HeaderCellContent
        align={align}
        allowsSorting={allowsSorting}
        sortDirection={sortDirection}
        overflow={overflow}
        textValue={textValue}
      >
        {children}
      </HeaderCellContent>
    </ElementType>
  );
});

export function headerCellClasses({
  align,
  allowsSorting,
  sortDirection,
  isFocusVisible,
  isHovered,
}: {
  align: ColumnAlignment | undefined;
  allowsSorting: boolean | undefined;
  sortDirection: SortDirection | undefined;
  isFocusVisible: boolean;
  isHovered: boolean;
}): string {
  return clsx(
    styles['sapphire-table__headCell'],
    styles['js-focus'],
    styles['js-hover'],
    getAlignmentStyles(align),
    {
      [styles['sapphire-table__headCell--sortable']]: allowsSorting,
      [styles['sapphire-table__headCell--sorted']]: Boolean(sortDirection),
      [styles['is-focus']]: isFocusVisible,
      [styles['is-hover']]: isHovered,
    }
  );
}

export function HeaderCellContent({
  align,
  sortDirection,
  allowsSorting,
  children,
  textValue,
  overflow,
}: {
  align: ColumnAlignment | undefined;
  allowsSorting: boolean | undefined;
  sortDirection: SortDirection | undefined;
  textValue: React.ReactNode;
  overflow: 'wrap' | 'truncate' | undefined;
  children: React.ReactNode;
}): React.JSX.Element {
  const iconSpan = allowsSorting ? (
    <span
      aria-hidden="true"
      className={clsx({
        [styles['sapphire-table-sortableIcon']]: !sortDirection,
        [styles['sapphire-table-sortedIcon']]: Boolean(sortDirection),
      })}
    >
      {sortDirection === 'descending' ? (
        <Icon size="md">
          <SortDescending />
        </Icon>
      ) : (
        <Icon size="md">
          <SortAscending />
        </Icon>
      )}
    </span>
  ) : null;

  return (
    <div className={styles['sapphire-table__headCell_container']}>
      {align === 'right' && iconSpan}
      <TooltipIfNeeded<HTMLTableCellElement> title={textValue}>
        {(ref, tooltipTriggerProps) => {
          const { ref: positionRef, ...restTooltipProps } =
            tooltipTriggerProps || {};
          return (
            <span
              ref={mergeRefs(ref, positionRef)}
              {...restTooltipProps}
              className={clsx({
                [styles['sapphire-table__headCell_content--ellipsed']]:
                  overflow === 'truncate',
              })}
            >
              {children}
            </span>
          );
        }}
      </TooltipIfNeeded>

      {align !== 'right' && iconSpan}
    </div>
  );
}
